import type { ApplePayElement, CardElement, CardElementOptions, PaymentSubmitResponse, TeyaBlocks } from '@teyaproduct/teya-blocks-js'
import { environment } from '../../../environments/environment'
import type { PaymentSession, PaymentSessionOptions } from '../PaymentSession'
import { PaymentSessionValidationError } from '../PaymentSession'
import type { TeyaConstructor } from './teya-client-loader.service'

export class TeyaSession implements PaymentSession {
    readonly gateway = 'teya' as const
    readonly walletPaymentAvailable: boolean
    readonly requiresFutureChargeConsent: boolean
    private readonly sdk: TeyaBlocks
    private element: CardElement | null = null
    private applePay: ApplePayElement | null = null
    private destroyed = false
    private valid = false
    private submitting = false
    private completed = false
    private validityHandler: (valid: boolean) => void = () => undefined
    private walletHandler: (id: string) => void = () => undefined
    private errorHandler: (message: string) => void = () => undefined
    private rejectReady: ((error: Error) => void) | null = null
    private rejectForCurrencyChoice: (() => void) | null = null

    private constructor(Client: TeyaConstructor, private readonly options: PaymentSessionOptions, private readonly saveBillingEmail: (email: string) => Promise<void>) {
        this.requiresFutureChargeConsent = options.usage === 'setup-card'
        this.walletPaymentAvailable = !this.requiresFutureChargeConsent
            && !options.requiresBillingEmail && Boolean(options.applePayWalletButtonSelector)
        this.sdk = new Client(options.intent.clientSecret, { developmentMode: !environment.production, locale: 'en-GB' })
    }

    static async create(Client: TeyaConstructor, options: PaymentSessionOptions, saveBillingEmail: (email: string) => Promise<void>): Promise<TeyaSession> {
        if (!options.intent.clientSecret?.trim()) {
            throw new Error('Teya session token is required')
        }
        const session = new TeyaSession(Client, options, saveBillingEmail)
        try {
            await session.mount()
            return session
        } catch (error) {
            session.destroy()
            throw error
        }
    }

    private mount(): Promise<void> {
        return new Promise((resolve, reject) => {
            const timer = setTimeout(() => reject(new Error('The payment form took too long to load. Please try again.')), 30000)
            this.rejectReady = error => {
                clearTimeout(timer)
                reject(error)
            }
            const callbacks: CardElementOptions = {
                onReady: () => {
                    clearTimeout(timer)
                    this.rejectReady = null
                    if (!this.destroyed) {
                        resolve()
                    }
                },
                onChange: (event: { complete: boolean }) => {
                    if (this.destroyed) {
                        return
                    }
                    this.valid = event.complete
                    this.validityHandler(event.complete)
                },
                onDccSelectionRequired: () => {
                    if (this.destroyed) {
                        return
                    }
                    this.rejectForCurrencyChoice?.()
                },
                onError: (error: { code?: string }) => {
                    if (this.destroyed) {
                        return
                    }
                    const message = error.code === 'validation_error'
                        ? 'Please complete your card details.'
                        : 'Your payment could not be completed. Please check your details and try again.'
                    if (this.rejectReady) {
                        this.rejectReady(new Error(message))
                    } else {
                        this.errorHandler(message)
                    }
                },
            }
            this.element = this.sdk.elements.create('card', callbacks)
            this.element.mount(this.options.cardFormSelector)
            this.mountApplePay()
        })
    }

    private mountApplePay(): void {
        const selector = this.options.applePayWalletButtonSelector
        if (!this.walletPaymentAvailable || !selector) {
            return
        }
        this.applePay = this.sdk.elements.create('applePay', {
            onClick: () => {
                if (this.destroyed || this.completed) {
                    return
                }
                this.submitting = true
                this.element?.update({ disabled: true })
            },
            onCancel: () => this.resumeCardEntry(),
            onError: () => this.walletPaymentFailed(),
            onSuccess: response => {
                if (this.destroyed || this.completed) {
                    return
                }
                try {
                    this.applePay?.update({ disabled: true })
                    this.walletHandler(this.complete(response))
                } catch {
                    this.walletPaymentFailed()
                }
            },
        })
        this.applePay.mount(selector)
    }

    private resumeCardEntry(): void {
        if (this.destroyed || this.completed) {
            return
        }
        this.submitting = false
        this.element?.update({ disabled: false })
        this.applePay?.update({ disabled: false })
    }

    private walletPaymentFailed(): void {
        if (this.destroyed || this.completed) {
            return
        }
        this.resumeCardEntry()
        this.errorHandler('Your payment could not be completed. Please check your details and try again.')
    }

    setOnValidityChange(handler: (valid: boolean) => void): void {
        this.validityHandler = handler
        handler(this.valid)
    }

    setOnWalletConfirmed(handler: (id: string) => void): void {
        this.walletHandler = handler
    }

    setOnError(handler: (message: string) => void): void {
        this.errorHandler = handler
    }

    async confirm(billingEmailAddress?: string): Promise<string> {
        if (this.completed) {
            return this.options.intent.id
        }
        if (this.destroyed || !this.element || this.submitting) {
            throw new Error('The payment form is not available')
        }
        if (this.requiresFutureChargeConsent && !this.options.hasFutureChargeConsent?.()) {
            throw new PaymentSessionValidationError('Please agree to the cancellation and no-show charge before continuing.')
        }
        const email = (billingEmailAddress ?? this.options.getBillingEmailAddress?.() ?? this.options.intent.bookingDetails?.bookingEmailAddress)?.trim()
        if (this.options.requiresBillingEmail && !email) {
            throw new PaymentSessionValidationError('Please enter a valid email address.')
        }
        this.submitting = true
        try {
            this.applePay?.update({ disabled: true })
            if (email) {
                await this.saveBillingEmail(email)
            }
            const currencyChoice = new Promise<never>((_resolve, reject) => {
                this.rejectForCurrencyChoice = () => reject(new PaymentSessionValidationError('Choose your payment currency, then continue.'))
            })
            const response = await Promise.race([this.element.submitPayment(), currencyChoice])
            return this.complete(response)
        } catch (error) {
            if (error instanceof PaymentSessionValidationError) {
                throw error
            }
            if (isValidationError(error)) {
                throw new PaymentSessionValidationError('Please complete your card details.')
            }
            throw new Error('Your payment could not be completed. Please check your details and try again.')
        } finally {
            this.rejectForCurrencyChoice = null
            if (!this.destroyed && !this.completed) {
                this.applePay?.update({ disabled: false })
            }
            this.submitting = false
        }
    }

    private complete(response: PaymentSubmitResponse): string {
        const transaction = response.checkout_payment_response?.online_transaction
        if (transaction?.status === 'FAILURE') {
            throw new Error('Payment declined')
        }
        this.completed = true
        return this.options.intent.id
    }

    destroy(): void {
        if (this.destroyed) {
            return
        }
        this.destroyed = true
        this.rejectReady?.(new Error('Payment form closed'))
        this.rejectReady = null
        this.element?.destroy()
        this.element = null
        this.applePay?.destroy()
        this.applePay = null
        this.sdk.destroy()
    }
}

function isValidationError(error: unknown): boolean {
    return typeof error === 'object' && error !== null && 'code' in error && error.code === 'validation_error'
}
